import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    alpha,
    Box,
    ButtonBase,
    Card,
    Chip,
    Collapse,
    Divider,
    Grid,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import moment from 'moment-timezone';
import React, { useMemo, useState } from 'react';
import {
    Job,
    JobItem,
    ProviderType,
} from '../../../../app/Slices/SevenDayApiTypes';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import {
    camelCaseToSentenceCase,
    camelCaseToTitleCase,
    capitalizeName,
    formatCurrency,
    sum,
} from '../../../../shared/utils';
import { ServiceType, Status } from '../../../enums/contractor';
import { groupJobItemsByTemplate } from '../../Reservations/ReservationRequest/ReservationJobs/ReservationJobForm';
import { theme } from '../../../../shared/theme';
import { SetMenuCourse } from '../../../enums/set-menu';
import { BookingSetMenu, SetMenuItem } from '../../../types/set-menu';
import { serviceTypeLeadingIcon } from '../../Contractors/contractorServiceIcons';
import { compareProviderServiceTypes } from '../../Reservations/ReservationRequest/ReservationJobs/reservationJobs.helpers';

type GroupedJobItem = {
    template: { id: number; name: string };
    jobItems: JobItem[];
};

type ModifiedJob = Omit<Job, 'items'> & {
    items: GroupedJobItem[];
    jobTotal: number;
};

const SET_MENU_COURSE_ORDER: { value: SetMenuCourse; label: string }[] = [
    { value: SetMenuCourse.Starter, label: 'Starters' },
    { value: SetMenuCourse.Main, label: 'Mains' },
    { value: SetMenuCourse.Side, label: 'Sides' },
    { value: SetMenuCourse.Dessert, label: 'Desserts' },
    { value: SetMenuCourse.Drink, label: 'Drinks' },
];

const groupSetMenuItemsByCourse = (items: SetMenuItem[]) =>
    SET_MENU_COURSE_ORDER.map((course) => ({
        ...course,
        items: items
            .filter((item) => item.course === course.value)
            .sort((a, b) => a.order - b.order),
    })).filter((group) => group.items.length > 0);

const shouldShowItemDescription = (name: string, description?: string) => {
    const normalizedDescription = description?.trim();
    if (!normalizedDescription) return false;
    return normalizedDescription.toLowerCase() !== name.trim().toLowerCase();
};

const getStatusChipColor = (status: Status) => {
    if (status === Status.Completed) return 'green';
    if (status === Status.Active) return 'green';
    if (status === Status.Declined) return 'gray';
    if (status === Status.Accepted) return 'yellow';
    if (status === Status.Pending) return 'blue';
    if (status === Status.PendingBooking) return 'blue';
    if (status === Status.PendingCompletion) return 'yellow';
    if (status === Status.Cancelled) return 'gray';
    return 'red';
};

const ReservationJobsCharges = ({
    jobs,
    serviceType,
}: {
    jobs: (Job | BookingSetMenu)[];
    serviceType?: ServiceType;
}) => {
    const [expandedJobs, setExpandedJobs] = useState<Record<number, boolean>>(
        {},
    );
    const isMobile = useMediaQuery('(max-width: 770px)');

    const modifiedJobs: (ModifiedJob | BookingSetMenu)[] = jobs
        .map((job: Job | BookingSetMenu) => {
            return job.providerType === ProviderType.Contractor
                ? {
                      ...job,
                      items: groupJobItemsByTemplate(job?.items || []),
                      jobTotal: sum(
                          (job?.items || []).map(
                              (item: JobItem) => item.total || 0,
                          ),
                      ),
                  }
                : job;
        })
        .sort((a, b) =>
            compareProviderServiceTypes(a.serviceType, b.serviceType),
        );

    const totalForAllJobs = useMemo(() => {
        const total = sum(
            modifiedJobs
                .filter(
                    (job) =>
                        job.status !== Status.Cancelled &&
                        job.status !== Status.Declined,
                )
                .map((job) =>
                    job.providerType === ProviderType.Contractor
                        ? job.jobTotal
                        : job.total || 0,
                ),
        );
        return formatCurrency(total);
    }, [modifiedJobs]);

    const toggleJobExpansion = (jobId: number) => {
        setExpandedJobs((prev) => ({
            ...prev,
            [jobId]: !prev[jobId],
        }));
    };

    return (
        <Grid>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {modifiedJobs.map(
                    (job: ModifiedJob | BookingSetMenu, index: number) => {
                        const isContractorJob =
                            job.providerType === ProviderType.Contractor;
                        const jobId = Number(job.id);
                        const isExpanded = !!expandedJobs[jobId];
                        const isInactive =
                            job.status === Status.Cancelled ||
                            job.status === Status.Declined;

                        const dateLabel = job.jobDate
                            ? moment(job.jobDate).format('DD/MM/YYYY')
                            : '';
                        const startTime = job.startTime;
                        const endTime = job.endTime;
                        const services = isContractorJob
                            ? job.services || []
                            : [];

                        const hasDetails =
                            (isContractorJob &&
                                ((job.items && job.items.length > 0) ||
                                    !!job.guestNotes)) ||
                            !isContractorJob;

                        const timeLabel =
                            startTime && endTime
                                ? `${startTime} - ${endTime}`
                                : startTime
                                  ? `${startTime}`
                                  : '';

                        const headerBg = isExpanded
                            ? theme.palette.action.hover
                            : 'transparent';

                        const ServiceTypeIcon = serviceTypeLeadingIcon(
                            job.serviceType,
                        );

                        const serviceTypeLabel =
                            job.serviceType !== ServiceType.Other
                                ? camelCaseToTitleCase(job.serviceType)
                                : 'Service';
                        const providerLabel =
                            job.providerType === ProviderType.Contractor
                                ? job.contractor?.name
                                : 'the host (Set Menu)';

                        return (
                            <Card
                                key={job.providerType + job.id + index}
                                sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                    boxShadow: 'none',
                                    overflow: 'hidden',
                                }}
                            >
                                <ButtonBase
                                    onClick={() =>
                                        hasDetails
                                            ? toggleJobExpansion(jobId)
                                            : null
                                    }
                                    disabled={!hasDetails}
                                    sx={{
                                        width: '100%',
                                        textAlign: 'left',
                                        display: 'block',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            p: isMobile ? 1.25 : 1.5,
                                            backgroundColor: headerBg,
                                            transition:
                                                'background-color 120ms ease',
                                            display: 'flex',
                                            flexDirection: isMobile
                                                ? 'column'
                                                : 'row',
                                            justifyContent: 'space-between',
                                            alignItems: isMobile
                                                ? 'stretch'
                                                : 'flex-start',
                                            gap: isMobile ? 1 : 2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.75,
                                                minWidth: 0,
                                                flex: 1,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent:
                                                        'space-between',
                                                    gap: 1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        minWidth: 0,
                                                        flexWrap: 'wrap',
                                                    }}
                                                >
                                                    <Typography
                                                        variant='body3'
                                                        sx={{ fontWeight: 900 }}
                                                    >
                                                        Job #{job.id}
                                                    </Typography>

                                                    <CustomChip
                                                        variant='outlined'
                                                        color={getStatusChipColor(
                                                            job.status as Status,
                                                        )}
                                                        sx={{
                                                            minWidth: 90,
                                                            fontSize: '10px',
                                                        }}
                                                        size='small'
                                                        label={camelCaseToTitleCase(
                                                            job.status,
                                                        ).toUpperCase()}
                                                    />
                                                </Box>

                                                {hasDetails ? (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            gap: 0.5,
                                                            color: theme.palette
                                                                .text.secondary,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant='caption'
                                                            sx={{
                                                                fontWeight: 700,
                                                                letterSpacing: 0.2,
                                                            }}
                                                        >
                                                            {isExpanded
                                                                ? 'Hide'
                                                                : 'View'}
                                                        </Typography>
                                                        {isExpanded ? (
                                                            <ExpandLessIcon fontSize='small' />
                                                        ) : (
                                                            <ExpandMoreIcon fontSize='small' />
                                                        )}
                                                    </Box>
                                                ) : null}
                                            </Box>

                                            <Box
                                                alignItems='center'
                                                sx={{
                                                    minWidth: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 18,
                                                        height: 18,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                        flexShrink: 0,
                                                        color: (t) =>
                                                            alpha(
                                                                t.palette
                                                                    .primary
                                                                    .main,
                                                                0.75,
                                                            ),
                                                    }}
                                                >
                                                    <ServiceTypeIcon
                                                        sx={{ fontSize: 14 }}
                                                    />
                                                </Box>
                                                <Typography
                                                    variant='body3'
                                                    sx={{
                                                        whiteSpace: isMobile
                                                            ? 'normal'
                                                            : 'nowrap',
                                                        textOverflow:
                                                            'ellipsis',
                                                        overflow: 'hidden',
                                                        maxWidth: isMobile
                                                            ? '100%'
                                                            : 320,
                                                        color: isInactive
                                                            ? theme.palette.text
                                                                  .secondary
                                                            : theme.palette.text
                                                                  .primary,
                                                    }}
                                                >
                                                    <Box
                                                        component='span'
                                                        sx={{ fontWeight: 700 }}
                                                    >
                                                        {serviceTypeLabel}
                                                    </Box>
                                                    <Box
                                                        component='span'
                                                        sx={{
                                                            fontStyle: 'italic',
                                                        }}
                                                    >
                                                        {` by ${providerLabel}`}
                                                    </Box>
                                                </Typography>
                                            </Box>

                                            <Stack
                                                direction='row'
                                                spacing={1}
                                                flexWrap='wrap'
                                                sx={{ mt: 0.25 }}
                                            >
                                                {dateLabel || timeLabel ? (
                                                    <Chip
                                                        size='small'
                                                        label={`${dateLabel}${timeLabel ? ` • ${timeLabel}` : ''}`}
                                                        sx={{
                                                            fontSize: 11,
                                                            height: 22,
                                                        }}
                                                    />
                                                ) : null}

                                                {services.map(
                                                    (service: string) => (
                                                        <Chip
                                                            key={service}
                                                            size='small'
                                                            label={camelCaseToSentenceCase(
                                                                service,
                                                            )}
                                                            sx={{
                                                                fontSize: 11,
                                                                height: 22,
                                                            }}
                                                        />
                                                    ),
                                                )}
                                            </Stack>
                                        </Box>

                                        <Box
                                            sx={{
                                                minWidth: isMobile
                                                    ? '100%'
                                                    : 140,
                                                display: 'flex',
                                                flexDirection: isMobile
                                                    ? 'row'
                                                    : 'column',
                                                justifyContent: isMobile
                                                    ? 'space-between'
                                                    : 'flex-start',
                                                alignItems: isMobile
                                                    ? 'center'
                                                    : 'flex-end',
                                                textAlign: isMobile
                                                    ? 'left'
                                                    : 'right',
                                                gap: isMobile ? 0.5 : 0.25,
                                                mt: isMobile ? 0.25 : 0,
                                            }}
                                        >
                                            <Typography
                                                variant='caption'
                                                sx={{
                                                    textTransform: 'uppercase',
                                                    fontWeight: 800,
                                                    fontSize: 10,
                                                    letterSpacing: 0.6,
                                                    color: theme.palette.text
                                                        .secondary,
                                                }}
                                            >
                                                Item fee (ex. GST)
                                            </Typography>

                                            <Typography
                                                variant='body3'
                                                sx={{
                                                    fontWeight: 900,
                                                    textDecoration: isInactive
                                                        ? 'line-through'
                                                        : 'none',
                                                    color: isInactive
                                                        ? theme.palette.text
                                                              .secondary
                                                        : theme.palette.text
                                                              .primary,
                                                }}
                                            >
                                                {formatCurrency(
                                                    (isContractorJob
                                                        ? job.jobTotal
                                                        : job.total) || 0,
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </ButtonBase>

                                {hasDetails ? (
                                    <Collapse
                                        in={isExpanded}
                                        timeout='auto'
                                        unmountOnExit
                                    >
                                        <Divider />
                                        <Box
                                            sx={{
                                                p: isMobile ? 1.25 : 1.5,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1.5,
                                                backgroundColor:
                                                    theme.palette.background
                                                        .paper,
                                            }}
                                        >
                                            {isContractorJob &&
                                            job.items &&
                                            job.items.length > 0 ? (
                                                <Box>
                                                    <Typography
                                                        variant='body3'
                                                        sx={{
                                                            fontWeight: 900,
                                                            mb: 0.75,
                                                        }}
                                                    >
                                                        Services
                                                    </Typography>

                                                    <Stack sx={{ gap: 1.25 }}>
                                                        {job.items.map(
                                                            (
                                                                group: GroupedJobItem,
                                                            ) => (
                                                                <Box
                                                                    key={
                                                                        group
                                                                            .template
                                                                            .id
                                                                    }
                                                                >
                                                                    <Typography
                                                                        variant='body3'
                                                                        sx={{
                                                                            fontWeight: 800,
                                                                            mb: 0.75,
                                                                            color: theme
                                                                                .palette
                                                                                .text
                                                                                .secondary,
                                                                        }}
                                                                    >
                                                                        {
                                                                            group
                                                                                .template
                                                                                .name
                                                                        }
                                                                    </Typography>

                                                                    <Stack
                                                                        sx={{
                                                                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                                                            borderRadius: 1.5,
                                                                            overflow:
                                                                                'hidden',
                                                                        }}
                                                                    >
                                                                        {group.jobItems.map(
                                                                            (
                                                                                item: JobItem,
                                                                                itemIndex: number,
                                                                            ) => (
                                                                                <Box
                                                                                    key={`${group.template.id}-${itemIndex}`}
                                                                                    sx={{
                                                                                        px: 1.25,
                                                                                        py: 1,
                                                                                        display:
                                                                                            'flex',
                                                                                        alignItems:
                                                                                            'flex-start',
                                                                                        justifyContent:
                                                                                            'space-between',
                                                                                        gap: 1,
                                                                                        backgroundColor:
                                                                                            itemIndex %
                                                                                                2 ===
                                                                                            0
                                                                                                ? theme
                                                                                                      .palette
                                                                                                      .background
                                                                                                      .paper
                                                                                                : theme
                                                                                                      .palette
                                                                                                      .action
                                                                                                      .hover,
                                                                                    }}
                                                                                >
                                                                                    <Box
                                                                                        sx={{
                                                                                            minWidth: 0,
                                                                                            flex: 1,
                                                                                        }}
                                                                                    >
                                                                                        <Typography
                                                                                            variant='body3'
                                                                                            component='div'
                                                                                            sx={{
                                                                                                fontWeight: 800,
                                                                                            }}
                                                                                        >
                                                                                            {
                                                                                                item.name
                                                                                            }
                                                                                        </Typography>

                                                                                        {shouldShowItemDescription(
                                                                                            item.name,
                                                                                            item.description,
                                                                                        ) ? (
                                                                                            <Typography
                                                                                                variant='body3'
                                                                                                component='div'
                                                                                                sx={{
                                                                                                    color: theme
                                                                                                        .palette
                                                                                                        .text
                                                                                                        .secondary,
                                                                                                    mt: 0.25,
                                                                                                }}
                                                                                            >
                                                                                                {
                                                                                                    item.description
                                                                                                }
                                                                                            </Typography>
                                                                                        ) : null}

                                                                                        <Typography
                                                                                            variant='caption'
                                                                                            sx={{
                                                                                                display:
                                                                                                    'block',
                                                                                                color: theme
                                                                                                    .palette
                                                                                                    .text
                                                                                                    .secondary,
                                                                                                mt: 0.5,
                                                                                                fontWeight: 700,
                                                                                            }}
                                                                                        >
                                                                                            {
                                                                                                item.quantity
                                                                                            }{' '}
                                                                                            ×{' '}
                                                                                            {formatCurrency(
                                                                                                item.cost ||
                                                                                                    0,
                                                                                            )}
                                                                                        </Typography>
                                                                                    </Box>

                                                                                    <Typography
                                                                                        variant='body3'
                                                                                        sx={{
                                                                                            fontWeight: 900,
                                                                                            textDecoration:
                                                                                                isInactive
                                                                                                    ? 'line-through'
                                                                                                    : 'none',
                                                                                            color: isInactive
                                                                                                ? theme
                                                                                                      .palette
                                                                                                      .text
                                                                                                      .secondary
                                                                                                : theme
                                                                                                      .palette
                                                                                                      .text
                                                                                                      .primary,
                                                                                            whiteSpace:
                                                                                                'nowrap',
                                                                                        }}
                                                                                    >
                                                                                        {formatCurrency(
                                                                                            item.total ||
                                                                                                0,
                                                                                        )}
                                                                                    </Typography>
                                                                                </Box>
                                                                            ),
                                                                        )}
                                                                    </Stack>
                                                                </Box>
                                                            ),
                                                        )}
                                                    </Stack>
                                                </Box>
                                            ) : null}
                                            {!isContractorJob && (
                                                <Stack>
                                                    <Typography
                                                        variant='body3'
                                                        sx={{
                                                            fontWeight: 900,
                                                            mb: 0.75,
                                                        }}
                                                    >
                                                        Services
                                                    </Typography>
                                                    <Typography
                                                        variant='body3'
                                                        sx={{
                                                            fontWeight: 800,
                                                            mb: 0.75,
                                                            color: theme.palette
                                                                .text.secondary,
                                                        }}
                                                    >
                                                        {job.setMenu.name}{' '}
                                                    </Typography>
                                                    <Typography
                                                        variant='caption'
                                                        sx={{
                                                            fontWeight: 700,
                                                            mb: 0.75,
                                                            color: theme.palette
                                                                .text.secondary,
                                                        }}
                                                    >
                                                        {job.quantity} x{' '}
                                                        {formatCurrency(
                                                            job.pricePerPerson,
                                                        )}{' '}
                                                    </Typography>

                                                    <Stack
                                                        sx={{
                                                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                                            borderRadius: 1.5,
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        {(() => {
                                                            const courseGroups =
                                                                groupSetMenuItemsByCourse(
                                                                    job.setMenu
                                                                        .items,
                                                                );
                                                            let rowOffset = 0;

                                                            return courseGroups.map(
                                                                (
                                                                    group,
                                                                    groupIndex,
                                                                ) => {
                                                                    const currentRowOffset =
                                                                        rowOffset;
                                                                    rowOffset +=
                                                                        group
                                                                            .items
                                                                            .length;

                                                                    return (
                                                                        <Box
                                                                            key={
                                                                                group.value
                                                                            }
                                                                        >
                                                                            {group.items.map(
                                                                                (
                                                                                    item,
                                                                                    index,
                                                                                ) => (
                                                                                    <Box
                                                                                        key={
                                                                                            item.id
                                                                                        }
                                                                                        sx={{
                                                                                            px: 1.25,
                                                                                            py: 1,
                                                                                            display:
                                                                                                'flex',
                                                                                            alignItems:
                                                                                                'flex-start',
                                                                                            justifyContent:
                                                                                                'space-between',
                                                                                            gap: 1,
                                                                                            borderTop:
                                                                                                groupIndex >
                                                                                                    0 &&
                                                                                                index ===
                                                                                                    0
                                                                                                    ? `1px solid ${theme.palette.custom.lightBorderColor}`
                                                                                                    : 'none',
                                                                                            backgroundColor:
                                                                                                (currentRowOffset +
                                                                                                    index) %
                                                                                                    2 ===
                                                                                                0
                                                                                                    ? theme
                                                                                                          .palette
                                                                                                          .background
                                                                                                          .paper
                                                                                                    : theme
                                                                                                          .palette
                                                                                                          .action
                                                                                                          .hover,
                                                                                        }}
                                                                                    >
                                                                                        <Box
                                                                                            sx={{
                                                                                                minWidth: 0,
                                                                                                flex: 1,
                                                                                            }}
                                                                                        >
                                                                                            {index ===
                                                                                            0 ? (
                                                                                                <Typography
                                                                                                    variant='body3'
                                                                                                    component='div'
                                                                                                    sx={{
                                                                                                        fontWeight: 800,
                                                                                                        color: theme
                                                                                                            .palette
                                                                                                            .text
                                                                                                            .secondary,
                                                                                                        mb: 0.25,
                                                                                                    }}
                                                                                                >
                                                                                                    {
                                                                                                        group.label
                                                                                                    }
                                                                                                </Typography>
                                                                                            ) : null}
                                                                                            <Typography
                                                                                                variant='body3'
                                                                                                component='div'
                                                                                                sx={{
                                                                                                    fontWeight: 800,
                                                                                                }}
                                                                                            >
                                                                                                {
                                                                                                    item.name
                                                                                                }
                                                                                            </Typography>

                                                                                            {shouldShowItemDescription(
                                                                                                item.name,
                                                                                                item.description,
                                                                                            ) ? (
                                                                                                <Typography
                                                                                                    variant='body3'
                                                                                                    component='div'
                                                                                                    sx={{
                                                                                                        color: theme
                                                                                                            .palette
                                                                                                            .text
                                                                                                            .secondary,
                                                                                                        mt: 0.25,
                                                                                                    }}
                                                                                                >
                                                                                                    {
                                                                                                        item.description
                                                                                                    }
                                                                                                </Typography>
                                                                                            ) : null}
                                                                                        </Box>
                                                                                    </Box>
                                                                                ),
                                                                            )}
                                                                        </Box>
                                                                    );
                                                                },
                                                            );
                                                        })()}
                                                    </Stack>
                                                </Stack>
                                            )}

                                            {job.guestNotes ? (
                                                <Box
                                                    sx={{
                                                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                                        borderRadius: 1.5,
                                                        p: 1.25,
                                                        backgroundColor:
                                                            theme.palette.action
                                                                .hover,
                                                    }}
                                                >
                                                    <Stack spacing={0.5}>
                                                        <Typography
                                                            variant='body3'
                                                            component='div'
                                                            sx={{
                                                                fontWeight: 900,
                                                                display:
                                                                    'block',
                                                            }}
                                                        >
                                                            Notes
                                                        </Typography>
                                                        <Typography
                                                            variant='body3'
                                                            component='div'
                                                            sx={{
                                                                display:
                                                                    'block',
                                                                color: theme
                                                                    .palette
                                                                    .text
                                                                    .secondary,
                                                                wordBreak:
                                                                    'break-word',
                                                            }}
                                                        >
                                                            {job.guestNotes}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            ) : null}
                                        </Box>
                                    </Collapse>
                                ) : null}
                            </Card>
                        );
                    },
                )}
            </Box>

            <Divider sx={{ mt: 2, mb: 1.5 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <Typography variant='body3' sx={{ fontWeight: 900 }}>
                        Estimated Total (ex. GST) *
                    </Typography>
                    <Typography
                        variant='body3'
                        sx={{ fontWeight: 900, textAlign: 'right' }}
                    >
                        {totalForAllJobs}
                    </Typography>
                </Box>

                <Typography
                    variant='body3'
                    sx={{ mt: 0.5, color: theme.palette.text.secondary }}
                >
                    {serviceType
                        ? `* Charges for ${serviceType} will be invoiced separately from the reservation expense, as the final amount may vary depending on the services provided on the day.`
                        : '* Charges for these services will be invoiced separately from the reservation expense, as the final amount may vary depending on the services provided on the day.'}
                </Typography>

                <Typography
                    variant='body3'
                    sx={{ mt: 0.5, color: theme.palette.text.secondary }}
                >
                    Note: Amount does not include declined jobs by the company
                    supplying the service.
                </Typography>
            </Box>
        </Grid>
    );
};

export default ReservationJobsCharges;
